// hooks/useUnitLease.ts - Owner/Manager операції з lease через UnitsController
/*
Цей хук покриває всі операції з UnitsController:
✅ Активний lease (GET /lease/active)
✅ Pending lease (GET /lease/pending)
✅ Історія lease (GET /lease/history)
✅ Створення pending lease + invite (POST /lease)
✅ Скасування pending lease (DELETE /lease/pending)
✅ Завершення lease (PATCH /lease/terminate)
Особливості:
Окремі query keys для кожного типу даних
Автоматична інвалідація кешу при змінах
Обробка 404 для pending lease (це нормально коли його немає)
Комбіновані хуки для зручності
Helper функції для частих перевірок
*/

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getActiveLeaseForUnitRequest,
  getLeaseHistoryForUnitRequest,
  terminateLeaseRequest,
  createPendingLeaseRequest,
  getPendingLeaseForUnitRequest,
  deletePendingLeaseRequest,
} from '@/api';
import { UnitLease, LeaseHistoryItem } from '@/types';

// === QUERY KEYS ===
export const unitLeaseKeys = {
  all: ['unit-lease'] as const,
  active: (propertyId: number, unitId: number) =>
    [...unitLeaseKeys.all, 'active', propertyId, unitId] as const,
  pending: (propertyId: number, unitId: number) =>
    [...unitLeaseKeys.all, 'pending', propertyId, unitId] as const,
  history: (propertyId: number, unitId: number) =>
    [...unitLeaseKeys.all, 'history', propertyId, unitId] as const,
};

// === АКТИВНИЙ LEASE ===

/**
 * Хук для отримання активного lease юніта (owner/manager)
 */
export const useActiveLease = (
  propertyId: number | undefined,
  unitId: number | undefined,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey:
      propertyId && unitId ? unitLeaseKeys.active(propertyId, unitId) : [],
    queryFn: () => {
      if (!propertyId || !unitId) {
        throw new Error('Property ID and Unit ID are required');
      }
      return getActiveLeaseForUnitRequest(propertyId, unitId);
    },
    enabled: !!(propertyId && unitId && options?.enabled !== false),
    select: (data) => data.data,
  });
};

// === PENDING LEASE ===

/**
 * Хук для отримання pending lease юніта (owner/manager)
 */
export const usePendingLease = (
  propertyId: number | undefined,
  unitId: number | undefined,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey:
      propertyId && unitId ? unitLeaseKeys.pending(propertyId, unitId) : [],
    queryFn: () => {
      if (!propertyId || !unitId) {
        throw new Error('Property ID and Unit ID are required');
      }
      return getPendingLeaseForUnitRequest(propertyId, unitId);
    },
    enabled: !!(propertyId && unitId && options?.enabled !== false),
    select: (data) => data.data,
    retry: (failureCount, error: any) => {
      // Не retry якщо pending lease не знайдено (404)
      if (error?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// === ІСТОРІЯ LEASE ===

/**
 * Хук для отримання історії lease юніта (owner/manager)
 */
export const useLeaseHistory = (
  propertyId: number | undefined,
  unitId: number | undefined,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey:
      propertyId && unitId ? unitLeaseKeys.history(propertyId, unitId) : [],
    queryFn: () => {
      if (!propertyId || !unitId) {
        throw new Error('Property ID and Unit ID are required');
      }
      return getLeaseHistoryForUnitRequest(propertyId, unitId);
    },
    enabled: !!(propertyId && unitId && options?.enabled !== false),
    select: (data) => data.data,
  });
};

// === СТВОРЕННЯ PENDING LEASE (ІНВАЙТ) ===

/**
 * Хук для створення pending lease + відправки invite (owner/manager)
 */
export const useCreatePendingLease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      propertyId: number;
      unitId: number;
      email: string;
      monthlyRent: number;
    }) => {
      const { propertyId, unitId, email, monthlyRent } = params;
      return createPendingLeaseRequest(propertyId, unitId, {
        email,
        monthlyRent,
      });
    },
    onSuccess: (data, variables) => {
      const { propertyId, unitId } = variables;

      // Додати правильний query key для списку юнітів
      queryClient.invalidateQueries({
        queryKey: ['units', propertyId], // Або який використовується в usePropertyUnits
      });

      // Інвалідуємо кеш pending lease
      queryClient.invalidateQueries({
        queryKey: unitLeaseKeys.pending(propertyId, unitId),
      });

      // Інвалідуємо кеш активного lease (якщо раптом щось змінилося)
      queryClient.invalidateQueries({
        queryKey: unitLeaseKeys.active(propertyId, unitId),
      });

      // Інвалідуємо кеш юнітів property (статус юніта міг змінитися)
      queryClient.invalidateQueries({
        queryKey: ['property-units', propertyId],
      });

      // Інвалідуємо кеш конкретного юніта
      queryClient.invalidateQueries({
        queryKey: ['unit-details', propertyId, unitId],
      });
    },
    onError: (error) => {
      console.error('Failed to create pending lease:', error);
    },
  });
};

// === СКАСУВАННЯ PENDING LEASE ===

/**
 * Хук для скасування pending lease (owner/manager)
 */
export const useCancelPendingLease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { propertyId: number; unitId: number }) => {
      const { propertyId, unitId } = params;
      return deletePendingLeaseRequest(propertyId, unitId);
    },
    onSuccess: (data, variables) => {
      const { propertyId, unitId } = variables;

      // Інвалідуємо кеш pending lease
      queryClient.invalidateQueries({
        queryKey: unitLeaseKeys.pending(propertyId, unitId),
      });

      // Інвалідуємо кеш юнітів property
      queryClient.invalidateQueries({
        queryKey: ['property-units', propertyId],
      });

      // Інвалідуємо кеш конкретного юніта
      queryClient.invalidateQueries({
        queryKey: ['unit-details', propertyId, unitId],
      });
    },
    onError: (error) => {
      console.error('Failed to cancel pending lease:', error);
    },
  });
};

// === ЗАВЕРШЕННЯ АКТИВНОГО LEASE ===

/**
 * Хук для завершення активного lease (owner/manager)
 */
export const useTerminateLease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      propertyId: number;
      unitId: number;
      endDate?: string;
    }) => {
      const { propertyId, unitId, endDate } = params;
      return terminateLeaseRequest(propertyId, unitId, endDate);
    },
    onSuccess: (data, variables) => {
      const { propertyId, unitId } = variables;

      // Інвалідуємо кеш активного lease
      queryClient.invalidateQueries({
        queryKey: unitLeaseKeys.active(propertyId, unitId),
      });

      // Інвалідуємо кеш історії lease
      queryClient.invalidateQueries({
        queryKey: unitLeaseKeys.history(propertyId, unitId),
      });

      // Інвалідуємо кеш юнітів property (статус зміниться)
      queryClient.invalidateQueries({
        queryKey: ['property-units', propertyId],
      });

      // Інвалідуємо кеш конкретного юніта
      queryClient.invalidateQueries({
        queryKey: ['unit-details', propertyId, unitId],
      });
    },
    onError: (error) => {
      console.error('Failed to terminate lease:', error);
    },
  });
};

// === КОМБІНОВАНІ ХУКИ ===

/**
 * Хук для отримання повної інформації про lease юніта (owner/manager)
 * Повертає активний lease, pending lease і історію
 */
export const useUnitLeaseInfo = (
  propertyId: number | undefined,
  unitId: number | undefined,
  options?: {
    enabled?: boolean;
    includeHistory?: boolean;
    includePending?: boolean;
  },
) => {
  const activeLease = useActiveLease(propertyId, unitId, {
    enabled: options?.enabled,
  });

  const pendingLease = usePendingLease(propertyId, unitId, {
    enabled: !!(options?.enabled && options?.includePending),
  });

  const leaseHistory = useLeaseHistory(propertyId, unitId, {
    enabled: !!(options?.enabled && options?.includeHistory),
  });

  return {
    // Активний lease
    activeLease: activeLease.data,
    activeLeaseLoading: activeLease.isLoading,
    activeLeaseError: activeLease.error,

    // Pending lease
    pendingLease: pendingLease.data,
    pendingLeaseLoading: pendingLease.isLoading,
    pendingLeaseError: pendingLease.error,

    // Історія lease
    leaseHistory: leaseHistory.data || [],
    historyLoading: leaseHistory.isLoading,
    historyError: leaseHistory.error,

    // Загальні стани
    isLoading:
      activeLease.isLoading ||
      (options?.includePending && pendingLease.isLoading) ||
      (options?.includeHistory && leaseHistory.isLoading),
    hasError:
      !!activeLease.error ||
      !!(
        options?.includePending &&
        pendingLease.error &&
        pendingLease.error?.response?.status !== 404
      ) ||
      !!(options?.includeHistory && leaseHistory.error),

    // Функції оновлення
    refetchActive: activeLease.refetch,
    refetchPending: pendingLease.refetch,
    refetchHistory: leaseHistory.refetch,
    refetchAll: () => {
      activeLease.refetch();
      if (options?.includePending) {
        pendingLease.refetch();
      }
      if (options?.includeHistory) {
        leaseHistory.refetch();
      }
    },
  };
};

// === HELPER ФУНКЦІЇ ===

/**
 * Перевіряє чи є юніт зайнятим (має активний lease)
 */
export const useIsUnitOccupied = (
  propertyId: number | undefined,
  unitId: number | undefined,
): boolean => {
  const { activeLease } = useUnitLeaseInfo(propertyId, unitId);
  return !!activeLease && activeLease.status === 'active';
};

/**
 * Перевіряє чи є pending lease для юніта
 */
export const useHasPendingLease = (
  propertyId: number | undefined,
  unitId: number | undefined,
): boolean => {
  const { pendingLease } = useUnitLeaseInfo(propertyId, unitId, {
    includePending: true,
  });
  return !!pendingLease && pendingLease.hasPendingLease;
};

/**
 * Отримує інформацію про поточний lease (активний або pending)
 * ОНОВЛЕНО: тепер працює з новою UnitLease структурою
 */
export const useCurrentLease = (
  propertyId: number | undefined,
  unitId: number | undefined,
) => {
  const { activeLease, pendingLease, activeLeaseLoading, activeLeaseError } =
    useUnitLeaseInfo(propertyId, unitId, { includePending: true });

  // ВИПРАВЛЕНО: UnitLease не має поля tenant, тільки tenantId
  return {
    lease: activeLease || (pendingLease?.hasPendingLease ? pendingLease.lease : null),
    monthlyRent: activeLease?.monthlyRent || pendingLease?.lease?.monthlyRent || '0',
    leaseStartDate: activeLease?.startDate || pendingLease?.lease?.startDate || null,
    leaseEndDate: activeLease?.endDate || pendingLease?.lease?.endDate || null,
    leaseId: activeLease?.id || pendingLease?.lease?.id || null,
    tenantId: activeLease?.tenantId || pendingLease?.lease?.tenantId || null,
    status: activeLease?.status || pendingLease?.lease?.status || null,
    isLoading: activeLeaseLoading,
    error: activeLeaseError,
    isPending: !activeLease && !!pendingLease?.hasPendingLease,
  };
};